import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { outpostStyles, FadingVideo, BlurText, OutpostNavbar } from '../components/OutpostShared';

const servicesData = [
  {
    title: "Brand Development",
    path: "/brand-development",
    desc: "Architecting iconic identities that command attention and endure."
  },
  {
    title: "Content Production",
    path: "/content-production",
    desc: "Captivating photography, video, and cinematic campaigns."
  },
  {
    title: "E-Commerce & Amazon",
    path: "/ecommerce-optimization",
    desc: "Dominating marketplaces with optimized brand touchpoints."
  },
  {
    title: "Digital Marketing",
    path: "/digital-marketing",
    desc: "Data-driven strategies turning attention into measurable revenue."
  },
  {
    title: "Social Media Marketing",
    path: "/social-media-marketing",
    desc: "Cultivating communities and scaling your digital influence."
  }
];

export default function Outpost() {
  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* SECTION 1 - HERO */}
      <section className="relative min-h-screen w-full flex flex-col items-center overflow-hidden">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_080021_d598092b-c4c2-4e53-8e46-94cf9064cd50.mp4"
          className="absolute left-1/2 top-0 -translate-x-1/2 object-cover object-top z-0"
          style={{ width: "120%", height: "120%" }}
          maxOpacity={0.15}
        />

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center pt-24 px-4 text-center w-full">
          <motion.div 
            initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
            animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
            className="liquid-glass rounded-full flex items-center p-1 pr-4 gap-3 mb-6"
          >
            <span className="bg-black text-white px-3 py-1 text-xs font-semibold rounded-full font-body">Agency</span>
            <span className="text-sm text-black/80 font-body">Setting new standards in digital commerce</span>
          </motion.div>

          <BlurText 
            text="Crafting Digital Worlds Scaling Modern Brands" 
            className="text-6xl md:text-7xl lg:text-[5.5rem] font-heading italic text-black leading-[0.8] max-w-2xl justify-center tracking-[-4px]"
          />

          <motion.p 
            initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
            animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: 'easeOut' }}
            className="mt-6 text-sm md:text-base text-black/80 max-w-2xl font-body font-light leading-tight"
          >
            Discover your brand's true potential. Our pioneering creative direction and breakthrough e-commerce strategies bring extraordinary growth within reach—secure and scalable.
          </motion.p>

          <motion.div 
            initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
            animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: 'easeOut' }}
            className="flex items-center justify-center gap-6 mt-6"
          >
            <button 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('expertise')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="liquid-glass-strong rounded-full px-5 py-2.5 text-sm font-medium text-black flex items-center gap-1 font-body hover:scale-105 transition-transform"
            >
              Explore Services
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
            </button>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 - SERVICES HUB */}
      <section id="expertise" className="relative min-h-screen w-full overflow-hidden px-8 md:px-16 lg:px-20 pt-24 pb-24 flex flex-col">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_094631_d30ab262-45ee-4b7d-99f3-5d5848c8ef13.mp4"
          className="absolute inset-0 w-full h-full object-cover z-0"
          maxOpacity={0.15}
        />

        <div className="relative z-10 flex flex-col min-h-full max-w-7xl mx-auto w-full">
          <div className="mb-16 text-center">
            <div className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest">// What We Offer</div>
            <h2 className="font-heading italic text-black text-6xl md:text-7xl lg:text-[6rem] leading-[0.9] tracking-[-3px]">
              Our Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service, idx) => (
              <Link 
                key={idx} 
                to={service.path}
                className="group liquid-glass p-8 rounded-[1.25rem] flex flex-col hover:bg-black/5 transition-colors border border-black/5 h-full min-h-[280px]"
              >
                <div className="text-black/20 font-heading italic text-5xl mb-6">0{idx + 1}</div>
                <h3 className="font-heading italic text-3xl text-black mb-3 tracking-[-1px] leading-tight group-hover:translate-x-2 transition-transform">
                  {service.title}
                </h3>
                <p className="text-sm text-black/70 font-body leading-relaxed mb-8">
                  {service.desc}
                </p>
                <div className="mt-auto flex items-center text-xs font-semibold uppercase tracking-widest text-black group-hover:text-black/60 transition-colors">
                  View Service
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}